// T8-3 — **THE ONE INTERCEPTOR THAT TURNS A REFUSAL INTO HER PURPLE BOX**, and it was inside
// App.tsx until ITEM 6 needed to pay for its lines (R37/R42/R43/R44's currency, one round on).
//
// The audit that built this found ~50 client call sites that can be handed a 402 and exactly ONE
// that knew what to do with one; the rest printed a bare sentence with nothing to buy. So the door
// is not a call site's job at all: every 402 the app receives raises ONE event, and the app root
// renders ONE box from it — which is what covers the fifty-first surface too.
//
// **IT WAS PURE SIDE EFFECTS ON `window`, WHICH MEANT A LIVE CHROME WAS THE ONLY INSTRUMENT THAT
// COULD REACH IT** — R26's exact position, and its exact answer: the window is a PARAMETER, so a
// gate RUNS this against a fake one and reads back what it hooked, what it dispatched, and (the
// half no browser check bothers with) what it deliberately did NOT dispatch.
//
// Two laws live in here and neither is obvious from a diff:
//   • **THE BOX ONLY OPENS FOR A DOOR SHE TOUCHED.** Half this app's opening requests are gated, so
//     without the gesture window a zero-tier member met the purple box on ARRIVAL, before pressing
//     anything. Measured: it did. The gesture is read when the request is SENT, so a slow generation
//     she asked for still counts as hers.
//   • **THE RESPONSE IS CLONED.** Reading the real body here would leave the caller holding a used
//     stream, and every one of those fifty call sites has its own path through the same response.

// ================================================================================================
// >>> HER ONBOARDING EMERGENCY (2026-08-19) — A GESTURE IS SPENT BY THE NAVIGATION IT CAUSES. <<<
//
// HER REPORT, from her own live walk as a new user: *"signing up with EMAIL takes her STRAIGHT to
// the membership tiers... clicking away lands on home where My Room re-raises the membership page, a
// trap with no path but payment."* Three live tickets are the same defect (#122, #125, #132).
//
// **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE CAUSE IS THIS WINDOW.** A brand-new free
// member's landing fires several MOUNT-TIME gated reads — `room:living`, `journal`, `library`,
// `drawer:verses`, `daily:guidance` were all captured answering 402 within a second of arrival — and
// **her own Continue tap is a gesture**, so every one of them fell inside the five seconds and the
// FIRST one home called `sendToMembership`.
//
// >>> AND THE REASON IT IS NEW IS THAT THE DOOR CHANGED UNDER THIS LAW. <<< The gesture window was
// written when a refusal raised a POPUP over the page she was standing on: annoying on arrival,
// dismissible, and she stayed put — which is exactly the failure the comment below records catching.
// **Her 2026-08-18 ruling replaced that popup with the membership PAGE, and `sendToMembership`
// NAVIGATES.** The same 402 that used to raise a box she could close now takes her off her own room,
// and after signup the gesture is GUARANTEED, because Continue is a tap.
//
// **THE DISCRIMINATOR IS THE NAVIGATION, AND IT IS EXACT RATHER THAN A SHORTER TIMEOUT.** *She
// reached for a locked door* and *a page she has just landed on quietly asked for something locked*
// are two different facts (R18), and only the first may move her. A tap that CAUSED a navigation has
// spent itself: everything firing afterwards is the new page introducing itself, never her reach.
// Shortening the window would only make the trap intermittent, which is worse than reliable.
//
// The stamp lives ON THE WINDOW rather than in this module's closure, for `__twelveGateHooked`'s own
// reason: a second copy of this module (a bundle split, a hot reload) would otherwise keep its own
// stamp and `spendGesture` would clear one nobody reads.
// ================================================================================================
export const LOCKED_DOOR_EVENT = "twelve:locked-door";
export const GESTURE_WINDOW_MS = 5000;
// What counts as her touching something. Capture phase and passive: this must never be able to
// change, delay or swallow an interaction — it only writes down when one happened.
export const GESTURE_EVENTS = ["pointerdown", "keydown", "touchstart", "submit"];

type Listener = (event: unknown) => void;

export type LockedDoorWindow = {
  fetch: (...args: never[]) => Promise<{ status: number; clone: () => { json: () => Promise<unknown> } }>;
  addEventListener: (type: string, fn: Listener, options?: unknown) => void;
  dispatchEvent: (event: unknown) => boolean;
  __twelveGateHooked?: boolean;
  /** When she last touched something. 0 means *nothing of hers is outstanding* — either she has not
   *  touched anything yet, or the tap she made has already been spent by the navigation it caused. */
  __twelveGestureAt?: number;
  /** Which page she is on, counted. Bumped by every navigation (see `spendGesture`). A request sent
   *  on one page whose refusal lands on another is the NEW page introducing itself, never her reach. */
  __twelveNavEpoch?: number;
};

/**
 * >>> THE GESTURE IS SPENT BY THE NAVIGATION IT CAUSED. <<<
 *
 * Called on every page change — `page` is where all five ways in end up (`goTo`, `enter`, a typed
 * URL, the back button, the tier bounce), so ONE effect on it covers every route without a second
 * roster of who must remember (R42's own argument for the title, one law over).
 *
 * `win` is a PARAMETER so a gate RUNS this against a fake window and reads the stamp back, which is
 * the whole reason this module was pulled out of App.tsx in the first place (R46/R26).
 */
export function spendGesture(win: LockedDoorWindow | null = browserWindow()) {
  if (!win) return false;
  const had = (win.__twelveGestureAt || 0) > 0;
  win.__twelveGestureAt = 0;
  // >>> AND THE PAGE COUNTER MOVES WITH IT, WHICH IS THE HALF THE MEASUREMENT FORCED. <<< Spending
  // the gesture is not enough on its own: a request SENT while her tap was still standing keeps that
  // permission until its answer comes back, so a drawer's mount read fired on the page she was
  // LEAVING still arrived carrying it. Measured at coordinates, that is exactly how a fresh signup
  // was still walked to the tier ladder — `SEND reached=true lock={feature:"journal"}`, from a read
  // nobody asked for. Counting the page makes the two facts separable (R18): the interceptor now
  // needs her touch AND the page not to have changed underneath the request.
  win.__twelveNavEpoch = (win.__twelveNavEpoch || 0) + 1;
  return had;
}

/** Has she touched something SINCE the last navigation, recently enough to still own it? Exported
 *  so the law is RUN rather than read out of a closure nothing can reach. */
export function askedByHer(win: LockedDoorWindow | null = browserWindow(), now = Date.now()) {
  const at = win?.__twelveGestureAt;
  // >>> PRESENCE, NEVER TRUTHINESS (R53/R136/R137). <<< This stamp rides `window`, which anything on
  // the page can write to, and a loose read coerces: `true > 0` is TRUE and `1000 - true` is 999, so
  // a value that is not a moment at all would authorise a navigation nobody asked for — this round's
  // own defect arriving through its own guard. Caught by the fixture before it shipped.
  if (typeof at !== "number" || !Number.isFinite(at) || at <= 0) return false;
  return now - at < GESTURE_WINDOW_MS;
}

const browserWindow = () => (typeof window === "undefined" ? null : (window as unknown as LockedDoorWindow));

// Raise the box for a lock the caller already has in its hand (a 200 that carries a refusal —
// T7-5/T8-3: Lala answers warmly and withholds the material, which is not a 402 at all). Returns
// whether it actually raised one, so a caller is never guessing.
// >>> **IT #174/#164 — `answered` IS THE DIFFERENCE BETWEEN A WALL AND AN ANSWER, AND UNTIL NOW
// NOTHING CARRIED IT.** <<<
//
// Two completely different things arrived through this one event. A **402** is a hard wall: the
// request was refused, there is nothing to read, and walking her to the membership page is the right
// and only thing to do. A **200 that carries a `lock`** is the opposite — Lala answered her warmly,
// in her own voice, and withheld only the gated material. This module's own header already says so:
// *"Lala answers warmly and withholds the material, which is not a 402 at all."*
//
// Both dispatched the same event, and the listener navigated on both. So a member who asked Lala
// about her petition got a real answer written into her thread and was **thrown out of that thread
// onto the white membership page before she could read a word of it.** She wrote in: *"Every time I
// type in something to ask Lala I am redirected to a white page with all of the different types of
// membership."* She was describing this line.
//
// `answered: true` says *she already has her reply* — raise the box, name the door, and LEAVE HER
// WHERE SHE IS. The 402 path passes nothing and navigates exactly as it always did.
export function raiseLockedDoor(
  lock: unknown,
  win: LockedDoorWindow | null = browserWindow(),
  { answered = false }: { answered?: boolean } = {},
) {
  if (!lock || !win) return false;
  win.dispatchEvent(new CustomEvent(LOCKED_DOOR_EVENT, { detail: { ...(lock as object), answered } }));
  return true;
}

// Hook `fetch` once per page. Idempotent by a flag ON THE WINDOW rather than in module scope: a
// second copy of this module (a bundle split, a hot reload) would otherwise wrap the wrapper, and
// every 402 would raise the box twice.
export function watchLockedDoors(win: LockedDoorWindow | null = browserWindow()) {
  if (!win || win.__twelveGateHooked) return false;
  win.__twelveGateHooked = true;

  // The stamp is on the WINDOW, so `spendGesture` reaches the same one this reads (see the header).
  win.__twelveGestureAt = 0;
  const mark = () => { win.__twelveGestureAt = Date.now(); };
  for (const type of GESTURE_EVENTS) win.addEventListener(type, mark, { capture: true, passive: true });

  const nativeFetch = win.fetch.bind(win);
  win.fetch = async (...args: never[]) => {
    // >>> TWO FACTS, AND BOTH ARE READ WHEN THE REQUEST IS SENT (R18). <<< The gesture is read here
    // rather than at the answer, so a slow generation she really asked for still counts as hers —
    // T8-3's own ruling, and it is untouched. The EPOCH is read here for the opposite reason: a
    // request sent on the page she is leaving must not be able to raise a door on the page she
    // lands on, and that is precisely the read that walked a brand-new member off her own room.
    const hers = askedByHer(win);
    const sentOn = win.__twelveNavEpoch || 0;
    const response = await nativeFetch(...args);
    if (response.status === 402 && hers && (win.__twelveNavEpoch || 0) === sentOn) {
      // Read the CLONE. Consuming the real body here would leave the caller with a used stream.
      response
        .clone()
        .json()
        .then((data: unknown) => {
          const lock = data as { locked?: boolean } | null;
          if (lock?.locked) raiseLockedDoor(lock, win);
        })
        .catch(() => {
          /* a 402 with no JSON body is not a locked door we can render; the caller's own path stands */
        });
    }
    return response;
  };
  return true;
}
